import React, { useEffect, useState } from "react";
import { db, storage } from "../firebase";
import { collection, query, where, orderBy, limit, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const ShopMessages = ({ shopId, shopName }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [caption, setCaption] = useState("");
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [indexBuilding, setIndexBuilding] = useState(false);
  const [indexUrl, setIndexUrl] = useState("");
  const [retryIn, setRetryIn] = useState(0);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      setError("");
      try {
        const feedRef = collection(db, "feedPosts");
        const q = query(feedRef, where("shopId", "==", shopId), orderBy("publishedAt", "desc"), limit(20));
        const snap = await getDocs(q);
        const loaded = [];
        snap.forEach((docSnap) => {
          loaded.push({ id: docSnap.id, ...docSnap.data() });
        });
        setPosts(loaded);
        setIndexBuilding(false);
        setIndexUrl("");
        setRetryAttempts(0);
      } catch (e) {
        const msg = e?.message || "";
        const isIndexIssue = e?.code === "failed-precondition" || msg.includes("requires an index") || msg.includes("index is currently building");
        if (isIndexIssue) {
          const m = msg.match(/https?:\/\/\S+/);
          const link = m ? m[0] : "";
          setIndexUrl(link);
          setIndexBuilding(true);
          setError("We bouwen op de achtergrond een index. We proberen het zo weer.");
          const attempts = retryAttempts + 1;
          setRetryAttempts(attempts);
          const waitSec = Math.min(30, 5 * attempts);
          setRetryIn(waitSec);
          let remaining = waitSec;
          const timer = setInterval(() => {
            remaining -= 1;
            setRetryIn((prev) => (prev > 0 ? prev - 1 : 0));
            if (remaining <= 0) clearInterval(timer);
          }, 1000);
          if (attempts <= 6) {
            setTimeout(() => setReloadTick((t) => t + 1), waitSec * 1000);
          }
        } else {
          setError("Fout bij laden feed: " + e.message);
        }
      }
      setLoading(false);
    };
    if (shopId) loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, success, reloadTick]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!caption.trim() && !image) {
      setError("Voeg een tekst of foto toe.");
      return;
    }
    setUploading(true);
    let imageUrl = "";
    try {
      if (image) {
        const imgRef = ref(storage, `feedPosts/${shopId}/${Date.now()}_${image.name}`);
        await uploadBytes(imgRef, image);
        imageUrl = await getDownloadURL(imgRef);
      }

      const postData = {
        shopId,
        shopName: shopName || "",
        caption: caption.trim(),
        imageUrl,
        publishedAt: serverTimestamp(),
        status: "published",
        likes: 0,
        likedBy: [],
      };

      await addDoc(collection(db, "feedPosts"), postData);

      setCaption("");
      setImage(null);
      setSuccess("Post succesvol geplaatst in de feed!");
    } catch (e) {
      setError("Fout bij plaatsen post: " + e.message);
    }
    setUploading(false);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <p style={{ color: "var(--ink-soft)", fontSize: 14, marginBottom: 12 }}>
          Deel nieuws, koffie, foto&apos;s of acties met klanten die bij jouw zaak sparen. Zij zien jouw posts als eerste in hun feed.
        </p>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Bijv. Nieuwe seizoensblend of actie van de week..."
            rows={3}
            className="form-input"
            style={{ resize: "vertical" }}
            disabled={uploading}
          />
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImage(e.target.files[0])}
            className="form-input"
            disabled={uploading}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={uploading}
          >
            {uploading ? "Bezig met uploaden..." : "Post plaatsen"}
          </button>
        </form>
        {error && (
          <div role="status" aria-live="polite" className={`alert ${indexBuilding ? 'alert-warning' : 'alert-danger'}`} style={{ marginTop: 12 }}>
            {error}
            {indexBuilding && (
              <div style={{ marginTop: 6, fontSize: 13 }}>
                {indexUrl ? (
                  <a href={indexUrl} target="_blank" rel="noreferrer">Open index status</a>
                ) : (
                  <span>Index aan het bouwen...</span>
                )}
                <span style={{ marginLeft: 6 }}>
                  {retryAttempts <= 6 ? `Automatische poging over ${retryIn}s.` : "Automatische pogingen gestopt."}
                </span>
                <button
                  type="button"
                  onClick={() => setReloadTick((t) => t + 1)}
                  className="btn btn-secondary"
                  style={{ marginLeft: 10, padding: "2px 8px" }}
                >
                  Opnieuw proberen
                </button>
              </div>
            )}
          </div>
        )}
        {success && <div className="alert alert-success" style={{ marginTop: 12 }}>{success}</div>}
      </div>
      <div>
        <h4>Jouw feed posts</h4>
        {loading ? (
          <div role="status" aria-live="polite">Laden...</div>
        ) : posts.length === 0 ? (
          <div style={{ color: "var(--ink-soft)" }}>Nog geen posts geplaatst.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {posts.map((post) => (
              <div key={post.id} style={{ background: "rgba(47, 62, 92, 0.04)", borderRadius: 12, padding: 14, border: "1px solid var(--hairline)" }}>
                <div style={{ fontSize: 15, marginBottom: 6 }}>{post.caption}</div>
                {post.imageUrl && (
                  <img src={post.imageUrl} alt="Feed post" style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, marginBottom: 6, objectFit: "cover" }} />
                )}
                <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>
                  {post.publishedAt && post.publishedAt.toDate().toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" })}
                  {post.likes != null ? ` · ${post.likes} likes` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopMessages;
